// ============================================================================
// INSTANCE ITEM - Individual entity with validation status and actions
// ============================================================================

import { Check, X } from 'lucide-react'
import type { EntityTreeInstance } from '@/lib/api/client'

interface InstanceItemProps {
  instance: EntityTreeInstance
  showValidationActions?: boolean
  onConfirm: (entityUri: string) => void
  onReject: (entityUri: string) => void
  onSelect?: (instance: EntityTreeInstance) => void
  isSelected?: boolean
}

export function InstanceItem({
  instance,
  showValidationActions = true,
  onConfirm,
  onReject,
  onSelect,
  isSelected = false,
}: InstanceItemProps) {
  const isPending = instance.status === 'pending'
  const isConfirmed = instance.status === 'confirmed'

  const handleClick = () => {
    onSelect?.(instance)
  }

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation()
    onConfirm(instance.uri)
  }

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation()
    onReject(instance.uri)
  }

  return (
    <div
      onClick={handleClick}
      className={`
        group flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-all
        ${isSelected
          ? 'bg-warm-100 border border-warm-300'
          : 'hover:bg-warm-50 border border-transparent'
        }
        ${isPending ? 'pr-1' : ''}
      `}
    >
      {/* Status indicator */}
      <span
        className={`
          w-2 h-2 rounded-full flex-shrink-0
          ${isConfirmed ? 'bg-market' : ''}
          ${isPending ? 'bg-amber-400 animate-pulse' : ''}
          ${instance.status === 'rejected' ? 'bg-warm-300' : ''}
        `}
      />

      {/* Label */}
      <span
        className={`
          flex-1 text-xs truncate
          ${isPending ? 'text-amber-600 italic' : 'text-warm-700'}
          ${instance.status === 'rejected' ? 'text-warm-400 line-through' : ''}
        `}
        title={instance.label}
      >
        {instance.label}
      </span>

      {/* Validation actions - show on hover for pending items */}
      {showValidationActions && isPending && (
        <div className="hidden group-hover:flex items-center gap-1">
          <button
            onClick={handleConfirm}
            className="w-5 h-5 flex items-center justify-center rounded bg-warm-100 text-warm-500 hover:bg-market/20 hover:text-market transition-colors"
            title="Confirm"
          >
            <Check className="w-3 h-3" strokeWidth={3} />
          </button>
          <button
            onClick={handleReject}
            className="w-5 h-5 flex items-center justify-center rounded bg-warm-100 text-warm-500 hover:bg-red-100 hover:text-red-500 transition-colors"
            title="Reject"
          >
            <X className="w-3 h-3" strokeWidth={3} />
          </button>
        </div>
      )}
    </div>
  )
}

export default InstanceItem
